import type {
  CreateTenantInput,
  DisableTenantInput,
  EndImpersonationInput,
  ImpersonateInput,
  InviteOperatorInput,
  OwnerRelinkInput,
  PatchSystemTemplateInput,
  PatchTenantInput,
  SystemTemplateInput,
} from "./validation";

export interface SystemTemplateDTO {
  readonly id: string;
  readonly category: string;
  readonly title: { it: string; en: string };
  readonly description: { it: string; en: string };
  readonly contentMarkdown: string;
  readonly suggestedDepartment?: string;
  readonly createdBy: string;
  readonly createdAt: unknown;
  readonly updatedAt: unknown;
}

async function bearer(
  url: string,
  idToken: string,
  init: {
    method?: string;
    headers?: Record<string, string>;
    body?: unknown;
  } = {},
): Promise<Response> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${idToken}`,
    ...(init.headers ?? {}),
  };
  const reqInit: RequestInit = {};
  if (init.method !== undefined) reqInit.method = init.method;
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
    reqInit.body = JSON.stringify(init.body);
  }
  reqInit.headers = headers;
  return fetch(url, reqInit);
}

export interface TenantSummary {
  readonly id: string;
  readonly displayName: string;
  readonly status: "active" | "disabled";
  readonly activeModules: ReadonlyArray<string>;
  readonly userCount: number;
  readonly lastActivity: unknown;
}

export interface OperatorSummary {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly status: "active" | "disabled" | "pending";
}

export interface AuditEntry {
  readonly id: string;
  readonly action: string;
  readonly actorUid: string;
  readonly actorEmail: string;
  readonly targetTenantId?: string;
  readonly targetUid?: string;
  readonly reason?: string;
  readonly timestamp: unknown;
}

export async function listTenants(
  apiBase: string,
  idToken: string,
): Promise<{ tenants: ReadonlyArray<TenantSummary> }> {
  const res = await bearer(`${apiBase}/api/superadmin/tenants`, idToken);
  if (!res.ok) throw new Error(`listTenants failed: ${res.status}`);
  return (await res.json()) as { tenants: ReadonlyArray<TenantSummary> };
}

export async function getTenant(
  apiBase: string,
  tid: string,
  idToken: string,
): Promise<{
  tenant: Record<string, unknown>;
  owners: ReadonlyArray<Record<string, unknown>>;
  counts: { users: number; owners: number };
}> {
  const res = await bearer(`${apiBase}/api/superadmin/tenants/${tid}`, idToken);
  if (!res.ok) throw new Error(`getTenant failed: ${res.status}`);
  return (await res.json()) as {
    tenant: Record<string, unknown>;
    owners: ReadonlyArray<Record<string, unknown>>;
    counts: { users: number; owners: number };
  };
}

export async function createTenant(
  apiBase: string,
  input: CreateTenantInput,
  idToken: string,
): Promise<{
  tenantId: string;
  ownerUid: string;
  magicLinkSentTo: string;
  warning?: string;
}> {
  const res = await bearer(`${apiBase}/api/superadmin/tenants`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`createTenant failed: ${res.status}`);
  return (await res.json()) as {
    tenantId: string;
    ownerUid: string;
    magicLinkSentTo: string;
    warning?: string;
  };
}

export async function patchTenant(
  apiBase: string,
  tid: string,
  input: PatchTenantInput,
  idToken: string,
): Promise<void> {
  const res = await bearer(`${apiBase}/api/superadmin/tenants/${tid}`, idToken, {
    method: "PATCH",
    body: input,
  });
  if (!res.ok) throw new Error(`patchTenant failed: ${res.status}`);
}

export async function disableTenant(
  apiBase: string,
  tid: string,
  input: DisableTenantInput,
  idToken: string,
): Promise<{ disabledUsers: number }> {
  const res = await bearer(`${apiBase}/api/superadmin/tenants/${tid}/disable`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`disableTenant failed: ${res.status}`);
  return (await res.json()) as { ok: boolean; disabledUsers: number };
}

export async function enableTenant(
  apiBase: string,
  tid: string,
  input: DisableTenantInput,
  idToken: string,
): Promise<{ enabledUsers: number }> {
  const res = await bearer(`${apiBase}/api/superadmin/tenants/${tid}/enable`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`enableTenant failed: ${res.status}`);
  return (await res.json()) as { ok: boolean; enabledUsers: number };
}

export async function ownerRelink(
  apiBase: string,
  tid: string,
  input: OwnerRelinkInput,
  idToken: string,
): Promise<{ sentTo: string; warning?: string }> {
  const res = await bearer(`${apiBase}/api/superadmin/tenants/${tid}/owner-relink`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`ownerRelink failed: ${res.status}`);
  return (await res.json()) as {
    sentTo: string;
    warning?: string;
  };
}

export async function listOperators(
  apiBase: string,
  idToken: string,
): Promise<{ operators: ReadonlyArray<OperatorSummary> }> {
  const res = await bearer(`${apiBase}/api/superadmin/operators`, idToken);
  if (!res.ok) throw new Error(`listOperators failed: ${res.status}`);
  return (await res.json()) as { operators: ReadonlyArray<OperatorSummary> };
}

export async function inviteOperator(
  apiBase: string,
  input: InviteOperatorInput,
  idToken: string,
): Promise<{ uid: string; magicLinkSentTo: string; warning?: string }> {
  const res = await bearer(`${apiBase}/api/superadmin/operators`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`inviteOperator failed: ${res.status}`);
  return (await res.json()) as {
    uid: string;
    magicLinkSentTo: string;
    warning?: string;
  };
}

export async function disableOperator(
  apiBase: string,
  uid: string,
  idToken: string,
): Promise<void> {
  const res = await bearer(`${apiBase}/api/superadmin/operators/${uid}/disable`, idToken, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`disableOperator failed: ${res.status}`);
}

export async function enableOperator(apiBase: string, uid: string, idToken: string): Promise<void> {
  const res = await bearer(`${apiBase}/api/superadmin/operators/${uid}/enable`, idToken, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`enableOperator failed: ${res.status}`);
}

export async function startImpersonation(
  apiBase: string,
  input: ImpersonateInput,
  idToken: string,
): Promise<{
  customToken: string;
  expiresInSeconds: number;
  expiresAt: number;
}> {
  const res = await bearer(`${apiBase}/api/superadmin/impersonate`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`startImpersonation failed: ${res.status}`);
  return (await res.json()) as {
    customToken: string;
    expiresInSeconds: number;
    expiresAt: number;
  };
}

export async function endImpersonation(
  apiBase: string,
  input: EndImpersonationInput,
  idToken: string,
): Promise<void> {
  const res = await bearer(`${apiBase}/api/superadmin/impersonate/end`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`endImpersonation failed: ${res.status}`);
}

export async function listAudit(
  apiBase: string,
  query: { action?: string },
  idToken: string,
): Promise<{ entries: ReadonlyArray<AuditEntry> }> {
  const q = query.action ? `?action=${encodeURIComponent(query.action)}` : "";
  const res = await bearer(`${apiBase}/api/superadmin/audit${q}`, idToken);
  if (!res.ok) throw new Error(`listAudit failed: ${res.status}`);
  return (await res.json()) as { entries: ReadonlyArray<AuditEntry> };
}

export interface EmailLogEntry {
  readonly id: string;
  readonly to: string;
  readonly toDomain: string;
  readonly subject: string;
  readonly kind: string;
  readonly tenantId: string | null;
  readonly status: string;
  readonly statusDetail: string | null;
  /** True when the address sits on a SES suppression list: accepted, never delivered. */
  readonly suppressed: boolean;
  readonly sentAt: unknown;
  readonly events?: ReadonlyArray<{
    readonly type: string;
    readonly at: unknown;
    readonly detail: string | null;
  }>;
}

export interface EmailStats {
  readonly total: number;
  readonly delivered: number;
  readonly bounced: number;
  readonly suppressed: number;
  readonly problemTenants: ReadonlyArray<string>;
}

export interface EmailLogQuery {
  readonly to?: string;
  readonly toDomain?: string;
  readonly tenantId?: string;
  readonly status?: string;
  readonly kind?: string;
  /** Mandatory: the search is audited, and tenant-scoped ones are mirrored to the customer. */
  readonly reason: string;
}

export async function listEmails(
  apiBase: string,
  query: EmailLogQuery,
  idToken: string,
): Promise<{ entries: ReadonlyArray<EmailLogEntry> }> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (typeof v === "string" && v !== "") params.set(k, v);
  }
  const res = await bearer(`${apiBase}/api/superadmin/emails?${params.toString()}`, idToken);
  if (!res.ok) throw new Error(`listEmails failed: ${res.status}`);
  return (await res.json()) as { entries: ReadonlyArray<EmailLogEntry> };
}

export async function getEmailStats(apiBase: string, idToken: string): Promise<EmailStats> {
  const res = await bearer(`${apiBase}/api/superadmin/emails/stats`, idToken);
  if (!res.ok) throw new Error(`getEmailStats failed: ${res.status}`);
  return (await res.json()) as EmailStats;
}

export async function listSystemTemplates(
  apiBase: string,
  idToken: string,
): Promise<{ templates: ReadonlyArray<SystemTemplateDTO> }> {
  const res = await bearer(`${apiBase}/api/superadmin/system-templates`, idToken);
  if (!res.ok) throw new Error(`listSystemTemplates failed: ${res.status}`);
  return (await res.json()) as { templates: ReadonlyArray<SystemTemplateDTO> };
}

export async function createSystemTemplate(
  apiBase: string,
  input: SystemTemplateInput,
  idToken: string,
): Promise<{ id: string }> {
  const res = await bearer(`${apiBase}/api/superadmin/system-templates`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`createSystemTemplate failed: ${res.status}`);
  return (await res.json()) as { id: string };
}

export async function patchSystemTemplate(
  apiBase: string,
  id: string,
  input: PatchSystemTemplateInput,
  idToken: string,
): Promise<void> {
  const res = await bearer(`${apiBase}/api/superadmin/system-templates/${id}`, idToken, {
    method: "PATCH",
    body: input,
  });
  if (!res.ok) throw new Error(`patchSystemTemplate failed: ${res.status}`);
}

export async function deleteSystemTemplate(
  apiBase: string,
  id: string,
  idToken: string,
): Promise<void> {
  const res = await bearer(`${apiBase}/api/superadmin/system-templates/${id}`, idToken, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`deleteSystemTemplate failed: ${res.status}`);
}
